/**
 * Componente: DoctorCard
 * Card para exibição de médico na listagem de profissionais
 */

import { useState, useCallback, memo } from 'react';
import { Stethoscope, Star, DollarSign, CheckCircle, Clock, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import type { DoctorDTO } from '@/services/doctors';
import { getDoctorAvatarSrc } from '@/services/doctors';
import { getDoctorCrmCardLabel } from '@/lib/doctorCrm';
import Badge from './Badge';

interface DoctorCardProps {
  doctor: DoctorDTO;
  onSelect?: (doctor: DoctorDTO) => void;
  style?: React.CSSProperties;
}

const SPECIALTY_COLORS: Record<string, string> = {
  Cardiologia: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
  Neurologia: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  Pediatria: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400',
  Dermatologia: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  Ortopedia: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  Psiquiatria: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400',
  Ginecologia: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400',
  'Clínica Geral': 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400',
};

function getSpecialtyColor(specialty: string) {
  return SPECIALTY_COLORS[specialty] ?? 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400';
}

function getInitials(name?: string) {
  if (!name) return 'DR';
  return name
    .split(' ')
    .slice(0, 2)
    .map((n) => n?.[0] ?? '')
    .join('')
    .toUpperCase();
}

export const DoctorCard = memo(function DoctorCard({ doctor, onSelect, style }: DoctorCardProps) {
  const navigate = useNavigate();
  const [imgError, setImgError] = useState(false);
  
  const displayName = doctor.user?.name ?? 'Médico';
  const initials = getInitials(displayName);
  const specialtyColorClass = getSpecialtyColor(doctor.specialty);
  const avatarSrc = getDoctorAvatarSrc(doctor);
  const showPhoto = !!avatarSrc && !imgError;
  const ratingsCount = doctor.analytics?.ratingsCount || 0;
  const patientsCount = doctor.analytics?.patientsCount || 0;
  const ratingLabel =
    doctor.analytics?.satisfactionRating !== null && doctor.analytics?.satisfactionRating !== undefined
      ? doctor.analytics.satisfactionRating.toFixed(1)
      : 'Sem avaliações';
  const crmLabel = getDoctorCrmCardLabel(doctor.crm, doctor.crmState);

  const handleSelect = useCallback(() => {
    onSelect?.(doctor);
  }, [onSelect, doctor]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      onSelect?.(doctor);
    }
  }, [onSelect, doctor]);

  const handleButtonClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect?.(doctor);
  }, [onSelect, doctor]);

  const handleScheduleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/agendamento/${doctor.id}`);
  }, [navigate, doctor.id]);

  return (
    <div
      style={style}
      className={cn(
        'group relative bg-white dark:bg-neutral-900 rounded-2xl border border-gray-100 dark:border-neutral-800',
        'p-5 transition-all duration-200',
        'hover:shadow-lg hover:shadow-teal-500/10 hover:-translate-y-0.5',
        'hover:border-teal-200 dark:hover:border-teal-800',
        'cursor-pointer',
      )}
      onClick={handleSelect}
      role="button"
      tabIndex={0}
      aria-label={`Ver perfil de ${displayName}`}
      onKeyDown={handleKeyDown}
    >
      {/* Approval badge */}
      {doctor.isApproved && (
        <div className="absolute top-4 right-4">
          <CheckCircle className="w-4 h-4 text-teal-500" />
        </div>
      )}

      <div className="flex items-start gap-4">
        {/* Avatar */}
        <div className="shrink-0">
          {showPhoto ? (
            <img
              src={avatarSrc!}
              alt={`Avatar de ${displayName}`}
              loading="lazy"
              decoding="async"
              width={56}
              height={56}
              className="w-14 h-14 rounded-xl object-cover shadow-md shadow-teal-200 dark:shadow-teal-900/40"
              onError={() => setImgError(true)}
            />
          ) : (
            <div className="w-14 h-14 rounded-xl bg-linear-to-br from-teal-400 to-teal-600 flex items-center justify-center shadow-md shadow-teal-200 dark:shadow-teal-900/40">
              <span className="text-white font-bold text-lg">{initials}</span>
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 dark:text-white text-sm truncate">
            Dr(a). {displayName}
          </h3>
          <div className="flex items-center gap-1.5 mt-1">
            <Stethoscope className="w-3 h-3 text-gray-400 shrink-0" />
            <span
              className={cn(
                'text-xs font-medium px-2 py-0.5 rounded-full',
                specialtyColorClass,
              )}
            >
              {doctor.specialty}
            </span>
          </div>
          {crmLabel && (
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{crmLabel}</p>
          )}
        </div>
      </div>

      {/* Biography */}
      {doctor.biography && (
        <p className="mt-3 text-xs text-gray-500 dark:text-gray-400 line-clamp-2 leading-relaxed">
          {doctor.biography}
        </p>
      )}

      {/* Footer */}
      <div className="mt-4 pt-3 border-t border-gray-100 dark:border-neutral-800 flex items-center justify-between">
        {/* Price */}
        <div className="flex items-center gap-1">
          <DollarSign className="w-3.5 h-3.5 text-teal-500" />
          <span className="text-sm font-semibold text-gray-900 dark:text-white">
            {doctor.consultationPrice
              ? `R$ ${doctor.consultationPrice.toFixed(2).replace('.', ',')}`
              : 'Sob consulta'}
          </span>
        </div>

        {/* Status */}
        <div className="flex items-center gap-1">
          {doctor.isApproved ? (
            <>
              <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
              <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                Disponível
              </span>
            </>
          ) : (
            <>
              <Clock className="w-3 h-3 text-amber-400" />
              <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">
                Pendente
              </span>
            </>
          )}
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
        <div className="rounded-lg bg-gray-50 dark:bg-neutral-800/70 p-2">
          <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
            <Star className={cn('w-3.5 h-3.5', ratingsCount > 0 ? 'fill-amber-400 text-amber-400' : 'text-gray-300')} />
            <span>Avaliação</span>
          </div>
          <p className="mt-1 font-semibold text-gray-900 dark:text-white">{ratingLabel}</p>
          <p className="text-[10px] text-gray-400">
            {ratingsCount === 1 ? '1 avaliação' : `${ratingsCount} avaliações`}
          </p>
        </div>
        <div className="rounded-lg bg-gray-50 dark:bg-neutral-800/70 p-2">
          <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
            <Stethoscope className="w-3.5 h-3.5 text-teal-500" />
            <span>Atendidos</span>
          </div>
          <p className="mt-1 font-semibold text-gray-900 dark:text-white">{patientsCount}</p>
          <p className="text-[10px] text-gray-400">
            {patientsCount === 1 ? 'paciente atendido' : 'pacientes atendidos'}
          </p>
        </div>
      </div>

      {/* Actions CTA */}
      <div className="mt-3 flex gap-2 transition-all duration-200">
        <button
          className="flex-1 py-2 rounded-xl bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 text-xs font-semibold hover:bg-teal-100 dark:hover:bg-teal-900/50 transition-colors"
          onClick={handleButtonClick}
        >
          Ver perfil →
        </button>
        <button
          className="flex-1 py-2 rounded-xl bg-teal-600 hover:bg-teal-700 text-white text-xs font-semibold transition-colors flex items-center justify-center gap-1"
          onClick={handleScheduleClick}
        >
          <Calendar className="w-3.5 h-3.5" />
          Agendar
        </button>
      </div>
    </div>
  );
});

// Re-export Badge for convenience
export { Badge };
